import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "../../components/Common/Card";
import Button from "../../components/Common/Button";
import api from "../../utils/api";
import {
  ArrowLeft,
  Edit,
  User,
  Calendar,
  MessageSquare,
  DollarSign,
  CheckCircle,
  Clock,
  Home,
} from "lucide-react";
import MeetingPreference from "../../components/Meeting/MeetingPreference";
import { formatStatus } from "../../utils/formatters";

const MeetingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMeeting();
  }, [id]);

  const fetchMeeting = async () => {
    try {
      const response = await api.get(`/meetings/${id}`);
      console.log(response.data);
      setMeeting(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Görüşme yüklenemedi:", error);
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Yükleniyor...</div>;
  }

  if (!meeting) {
    return <div className="text-center py-8">Görüşme bulunamadı</div>;
  }

  const getOutcomeColor = (outcome) => {
    switch (outcome) {
      case "Satış":
        return "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800";
      case "Takip Edilecek":
        return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800";
      case "Olumsuz":
        return "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600";
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate("/meetings")}
        >
          <ArrowLeft size={16} />
          Geri
        </Button>
        <div className="flex gap-2">
          {!meeting.is_completed && (
            <Button
              size="sm"
              variant="success"
              onClick={() => navigate(`/meetings/${id}/complete`)}
            >
              <CheckCircle size={16} />
              Tamamla
            </Button>
          )}
          <Button size="sm" onClick={() => navigate(`/meetings/${id}/edit`)}>
            <Edit size={16} />
            Düzenle
          </Button>
        </div>
      </div>

      {/* Header */}
      <Card className="mb-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Görüşme Detayı
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  meeting.is_completed
                    ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                    : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
                }`}
              >
                {meeting.is_completed ? "Tamamlandı" : "Bekliyor"}
              </span>
              {meeting.outcome && (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium border ${getOutcomeColor(
                    meeting.outcome
                  )}`}
                >
                  {meeting.outcome}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-4 text-gray-600 dark:text-gray-400">
              <div className="flex items-center gap-2">
                <Calendar size={18} />
                <span>{new Date(meeting.date).toLocaleString("tr-TR")}</span>
              </div>
              <div className="flex items-center gap-2">
                <MessageSquare size={18} />
                <span>{formatStatus(meeting.type)}</span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Müşteri Bilgileri */}
        <Card title="Müşteri Bilgileri">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
              <User size={24} className="text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="font-semibold text-lg text-gray-900 dark:text-white">
                {meeting.customer?.name} {meeting.customer?.surname}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {formatStatus(meeting.customer?.customer_type)}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="w-full"
            onClick={() => navigate(`/customers/${meeting.customer?.id}`)}
          >
            Müşteri Detayına Git
          </Button>
        </Card>

        {/* Temsilci Bilgileri */}
        <Card title="Satış Temsilcisi">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
              <User size={24} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="font-semibold text-lg text-gray-900 dark:text-white">{meeting.user?.name}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Satış Temsilcisi</p>
            </div>
          </div>
        </Card>

        {/* Durum Bilgileri */}
        <Card title="Durum">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              {meeting.is_completed ? (
                <CheckCircle size={24} className="text-green-600" />
              ) : (
                <Clock size={24} className="text-yellow-600" />
              )}
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Görüşme Durumu</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {meeting.is_completed ? "Tamamlandı" : "Bekliyor"}
                </p>
              </div>
            </div>
            {meeting.follow_up_date && (
              <div className="flex items-center gap-3">
                <Calendar size={24} className="text-blue-600" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Geri Dönüş Tarihi</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {new Date(meeting.follow_up_date).toLocaleDateString(
                      "tr-TR"
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Görüşme Detayları */}
      {meeting.customer_property_preferences_id ? (
        <Card title="Görüşme Detayları" className="mb-6">
          <MeetingPreference
            PropertyPreferencesId={meeting.customer_property_preferences_id}
          />
        </Card>
      ) : null}

      {/* İlgilenilen Portföyler */}
      {meeting.interested_portfolios && meeting.interested_portfolios.length > 0 && (
        <Card title="🏠 İlgilenilen Portföyler" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {meeting.interested_portfolios.map((portfolio) => (
              <div
                key={portfolio.id}
                className="flex items-center gap-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg cursor-pointer hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-all"
                onClick={() => navigate(`/portfolios/${portfolio.id}`)}
              >
                <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                  <Home size={20} className="text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">{portfolio.title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {portfolio.city?.name} {portfolio.district?.name && `/ ${portfolio.district.name}`}
                  </p>
                  {portfolio.list_price && (
                    <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 mt-0.5">
                      {parseFloat(portfolio.list_price).toLocaleString('tr-TR')} ₺
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* İlgilenilen Projeler/Portföyler */}
      {meeting.projects_or_properties_of_interest &&
        meeting.projects_or_properties_of_interest.length > 0 && (
          <Card title="İlgilenilen Projeler/Portföyler" className="mb-6">
            <div className="space-y-2">
              {meeting.projects_or_properties_of_interest.map((item, index) => (
                <div key={index} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="font-medium text-gray-900 dark:text-gray-100">{item}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

      {/* Görüşme Notları */}
      {meeting.notes && (
        <Card title="Görüşme Notları">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{meeting.notes}</p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default MeetingDetail;
